import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { Activity, BookOpen, List, Calendar } from 'lucide-react'
import { usePresalesDashboardStore } from '../store'
import { KR3DemoDetailModal } from './KR3DemoDetail'

export function EngineRoomSection() {
  const { kr3, kr4, isLoadingKRs } = usePresalesDashboardStore()
  const [showKR3Demos, setShowKR3Demos] = useState(false)

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-muted)]">
          Engine Room
        </h2>
        <Tooltip
          content={
            <div className="max-w-xs">
              <div className="font-semibold mb-1">Operational capacity and readiness</div>
              <div className="text-xs">
                Are we running at the right capacity? Is the team equipped with the right tools?
              </div>
            </div>
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* KR3: Presales Capacity & Utilization */}
        <CapacityCard kr3={kr3} isLoading={isLoadingKRs} onViewDemos={() => setShowKR3Demos(true)} />

        {/* KR4: Enterprise Playbook */}
        <PlaybookCard kr4={kr4} isLoading={isLoadingKRs} />
      </div>

      <KR3DemoDetailModal isOpen={showKR3Demos} onClose={() => setShowKR3Demos(false)} />
    </div>
  )
}

// ========== KR3: Capacity & Utilization ==========

const MONTH_SHORT: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
  '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
}

function CapacityCard({ kr3, isLoading, onViewDemos }: { kr3: any; isLoading: boolean; onViewDemos: () => void }) {
  if (isLoading || !kr3) {
    return (
      <Card className="overflow-hidden">
        <div className="p-5 space-y-4">
          <div className="h-4 bg-[var(--bg-secondary)] animate-pulse rounded w-48" />
          <div className="h-24 bg-[var(--bg-secondary)] animate-pulse rounded" />
          <div className="h-8 bg-[var(--bg-secondary)] animate-pulse rounded w-32" />
        </div>
      </Card>
    )
  }

  const { baseline, target, total_demos, monthly_breakdown, breakdown_by_owner } = kr3
  const months = (monthly_breakdown || []) as Array<{
    month: string; total_demos: number; demos_per_person: number; source: string; by_owner: Record<string, number>
  }>

  // Collect unique people across all months
  const people = Array.from(new Set(months.flatMap(m => Object.keys(m.by_owner || {})))).sort()
  const PERSON_COLORS = ['bg-violet-500', 'bg-sky-500', 'bg-emerald-500', 'bg-amber-500']
  const PERSON_TEXT_COLORS = ['text-violet-400', 'text-sky-400', 'text-emerald-400', 'text-amber-400']

  // Bar chart scale: max of target or highest individual person count
  const maxBar = Math.max(target, ...months.flatMap(m => people.map(p => (m.by_owner || {})[p] || 0)), 1)

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR3</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            Presales Demos per Person
          </span>
        </div>
        <Tooltip
          content={
            <div className="space-y-2 max-w-xs">
              <div className="font-semibold">How it works:</div>
              <div className="text-xs">
                Tracks demos completed per person per month. Baseline: {baseline}/mo, Target: {target}/mo (+20%).
              </div>
              <div className="text-xs mt-1">
                Data sourced from calendar events tagged as &ldquo;Demo&rdquo;. Jan &amp; early Feb use manual counts from before calendar sync.
              </div>
            </div>
          }
        />
      </div>

      <div className="p-5 space-y-4">
        {/* Headline: total demos this quarter */}
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold text-[var(--text-primary)]">
            {total_demos}
          </span>
          <span className="text-sm text-[var(--text-muted)]">
            demos this quarter
          </span>
        </div>

        {/* Per-person breakdown */}
        {breakdown_by_owner && breakdown_by_owner.length > 0 && (
          <div className="space-y-1">
            {breakdown_by_owner.map((owner: any) => (
              <div key={owner.owner} className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-secondary)]">{owner.owner}</span>
                <span className="font-semibold text-[var(--text-primary)]">{owner.demos_count} demos</span>
              </div>
            ))}
          </div>
        )}

        {/* Monthly breakdown bars — per person */}
        <div className="space-y-3 pt-2 border-t border-[var(--border-color)]">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Demos per Month
          </span>
          {months.map((m: any) => {
            const monthLabel = MONTH_SHORT[m.month.split('-')[1]] || m.month

            return (
              <div key={m.month}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-[var(--text-secondary)] font-medium">{monthLabel}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[var(--text-muted)]">
                      {m.total_demos} total
                    </span>
                    {m.source !== 'db' && (
                      <span className="text-[10px] text-[var(--text-muted)]" title="Includes manual pre-sync count">*</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  {people.map((person, idx) => {
                    const count = (m.by_owner || {})[person] || 0
                    const pct = maxBar > 0 ? (count / maxBar) * 100 : 0
                    const hitTarget = count >= target
                    const barColor = hitTarget ? 'bg-green-500' : PERSON_COLORS[idx % PERSON_COLORS.length]
                    const textColor = hitTarget ? 'text-green-400' : PERSON_TEXT_COLORS[idx % PERSON_TEXT_COLORS.length]
                    const firstName = person.split(' ')[0]

                    return (
                      <div key={person} className="flex items-center gap-2">
                        <span className={`text-[10px] w-12 shrink-0 truncate ${PERSON_TEXT_COLORS[idx % PERSON_TEXT_COLORS.length]}`}>
                          {firstName}
                        </span>
                        <div className="flex-1 h-4 bg-[var(--bg-tertiary)] rounded overflow-hidden relative">
                          <div
                            className="absolute top-0 bottom-0 w-px bg-yellow-500/60 z-10"
                            style={{ left: `${maxBar > 0 ? (target / maxBar) * 100 : 0}%` }}
                          />
                          <div
                            className={`h-full ${barColor} rounded transition-all duration-500`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className={`text-[11px] font-semibold w-5 text-right shrink-0 ${textColor}`}>
                          {count}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-[10px] text-[var(--text-muted)]">
          {people.map((person, idx) => (
            <div key={person} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-sm ${PERSON_COLORS[idx % PERSON_COLORS.length]}`} />
              <span>{person.split(' ')[0]}</span>
            </div>
          ))}
          <div className="flex items-center gap-1">
            <div className="w-3 h-px bg-yellow-500/60" />
            <span>Target ({target})</span>
          </div>
          {months.some((m: any) => m.source !== 'db') && (
            <span>* manual count</span>
          )}
        </div>

        {/* Latest demo date */}
        {kr3.latest_demo_date && (
          <div className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
            <Calendar className="h-3 w-3" />
            Last demo: {new Date(kr3.latest_demo_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
          </div>
        )}

        <button
          onClick={onViewDemos}
          className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          <List className="h-3 w-3" />
          View Demos
        </button>
      </div>
    </Card>
  )
}

// ========== KR4: Enterprise Playbook ==========

function formatRelativeTime(dateStr: string | null): { text: string; isStale: boolean } {
  if (!dateStr) return { text: 'never updated', isStale: true }
  const now = new Date()
  const updated = new Date(dateStr + 'Z') // UTC from DB
  const diffMs = now.getTime() - updated.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return { text: 'today', isStale: false }
  if (diffDays === 1) return { text: 'yesterday', isStale: false }
  if (diffDays < 7) return { text: `${diffDays}d ago`, isStale: false }
  if (diffDays < 30) return { text: `${Math.floor(diffDays / 7)}w ago`, isStale: true }
  return { text: `${Math.floor(diffDays / 30)}mo ago`, isStale: true }
}

function DeliverableSlider({ item, onSave }: { item: any; onSave: (id: string, pct: number) => Promise<void> }) {
  const [value, setValue] = useState(item.percentage)

  // Sync if parent data changes (e.g. after optimistic revert)
  if (item.percentage !== value && !document.activeElement?.closest(`[data-deliverable="${item.id}"]`)) {
    setValue(item.percentage)
  }

  const fillColor = value === 100 ? '#22c55e' : '#3b82f6'
  const { text: updatedText, isStale } = formatRelativeTime(item.updated_at)

  return (
    <div data-deliverable={item.id} className="space-y-1">
      <div className="flex items-center justify-between">
        {item.description ? (
          <Tooltip content={<div className="max-w-xs text-xs">{item.description}</div>}>
            <span className={`text-sm cursor-help border-b border-dotted border-[var(--text-muted)] ${value === 100 ? 'text-green-400' : 'text-[var(--text-primary)]'}`}>
              {item.name}
            </span>
          </Tooltip>
        ) : (
          <span className={`text-sm ${value === 100 ? 'text-green-400' : 'text-[var(--text-primary)]'}`}>
            {item.name}
          </span>
        )}
        <div className="flex items-center gap-2">
          <span className={`text-[10px] ${isStale ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
            {isStale && value < 100 ? '⚠ ' : ''}{updatedText}
          </span>
          <span className={`text-xs font-medium tabular-nums ${value === 100 ? 'text-green-400' : value > 0 ? 'text-blue-400' : 'text-[var(--text-muted)]'}`}>
            {value}%
          </span>
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        onChange={(e) => setValue(parseInt(e.target.value))}
        onMouseUp={() => onSave(item.id, value)}
        onTouchEnd={() => onSave(item.id, value)}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:active:cursor-grabbing"
        style={{
          background: `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${value}%, rgba(100,116,139,0.3) ${value}%, rgba(100,116,139,0.3) 100%)`,
        }}
        title={`${item.name}: ${value}%`}
      />
    </div>
  )
}

function formatQuarterLabel(quarter: string): string {
  const [year, q] = quarter.split('-')
  return `${q} ${year}`
}

function PlaybookCard({ kr4, isLoading }: { kr4: any; isLoading: boolean }) {
  const { saveDeliverablePercentage } = usePresalesDashboardStore()

  if (isLoading || !kr4) {
    return (
      <Card className="overflow-hidden">
        <div className="p-5 space-y-4">
          <div className="h-4 bg-[var(--bg-secondary)] animate-pulse rounded w-48" />
          <div className="h-24 bg-[var(--bg-secondary)] animate-pulse rounded" />
          <div className="h-8 bg-[var(--bg-secondary)] animate-pulse rounded w-32" />
        </div>
      </Card>
    )
  }

  const { completion_percent, deliverables, completed_count, total_count, quarter } = kr4
  const quarterLabel = quarter ? formatQuarterLabel(quarter) : 'Deliverables'

  if (total_count === 0) {
    return (
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-blue-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR4</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            {quarterLabel} Deliverables
          </span>
        </div>
        <div className="p-5">
          <div className="text-center py-6">
            <div className="text-amber-400 text-sm font-medium mb-2">No deliverables set for {quarterLabel}</div>
            <div className="text-xs text-[var(--text-muted)]">
              New quarter deliverables need to be configured.
            </div>
          </div>
        </div>
      </Card>
    )
  }

  const progressColor =
    completion_percent >= 100
      ? 'bg-green-500'
      : completion_percent >= 75
      ? 'bg-blue-500'
      : completion_percent >= 50
      ? 'bg-yellow-500'
      : 'bg-red-500'

  const textColor =
    completion_percent >= 100
      ? 'text-green-400'
      : completion_percent >= 75
      ? 'text-blue-400'
      : completion_percent >= 50
      ? 'text-yellow-400'
      : 'text-red-400'

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-color)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-blue-400" />
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">KR4</span>
          <span className="text-sm font-medium text-[var(--text-primary)]">
            {quarterLabel} Deliverables
          </span>
        </div>
        <span className="text-xs text-[var(--text-muted)]">{completed_count}/{total_count} complete</span>
      </div>

      <div className="p-5 space-y-4">
        {/* Main metric */}
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-bold ${textColor}`}>
            {completion_percent}%
          </span>
          <span className="text-sm text-[var(--text-muted)]">
            avg completion
          </span>
        </div>

        {/* Overall progress bar */}
        <div>
          <div className="relative h-3 bg-[var(--bg-tertiary)] rounded-full overflow-hidden">
            <div
              className={`h-full ${progressColor} rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(completion_percent, 100)}%` }}
            />
          </div>
        </div>

        {/* Deliverable sliders */}
        <div className="space-y-3 pt-2 border-t border-[var(--border-color)]">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Deliverables
          </span>
          {deliverables.map((item: any) => (
            <DeliverableSlider
              key={item.id}
              item={item}
              onSave={saveDeliverablePercentage}
            />
          ))}
        </div>
      </div>
    </Card>
  )
}
